import { useState, useCallback } from 'react';
import {
  closestCorners,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  DropAnimation,
} from '@dnd-kit/core';
import type { CollisionDetection } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Employee, Company } from '../types';
import { sortItemsByEmployeeName } from '../utils/employeeSort';
import { PostgresApiClient } from '../services/postgresClient';
import { moveEmployee as dbMoveEmployee } from '../services/rhDataService';
import type { CompanyNotificationPayload } from '../utils/employeeNotificationMessages';
import { buildEmployeeMoveNotification } from '../utils/employeeNotificationMessages';

/** Localiza a coluna (container) de um id de cartão ou coluna. */
export function findContainer(id: string, items: Record<string, Employee[]>): string | undefined {
  if (id in items) return id;
  return Object.keys(items).find((key) => items[key].find((q) => q.id === id));
}

/** closestCorners ignora droppables vazios; prioriza ponteiro e interseção de retângulos. */
export const boardCollisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  const rect = rectIntersection(args);
  if (rect.length > 0) return rect;
  return closestCorners(args);
};

export const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.5' } },
  }),
};

export interface UseBoardDnDParams {
  items: Record<string, Employee[]>;
  setItems: React.Dispatch<React.SetStateAction<Record<string, Employee[]>>>;
  columns: { id: string; title: string; color?: string }[];
  columnKeyMap: Record<string, string>;
  afastamentoDates: Record<string, string>;
  setAfastamentoDates: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  currentCompany: Company | null;
  getSB: () => PostgresApiClient;
  persistSettingIfAuthenticated: (key: string, value: unknown, companyId?: string) => Promise<void>;
  getVerifiedDate: () => Date;
  addLog: (action: string, details: string) => void;
  broadcastNotifications: (payloads: CompanyNotificationPayload[]) => Promise<void>;
  /** Bloqueia drag-and-drop quando o usuário não pode editar colaboradores. */
  canEditEmployees?: boolean;
}

/**
 * Drag-and-drop do board: sensores, handlers e persistência de movimentação no Postgres.
 */
export function useBoardDnD({
  items,
  setItems,
  columns,
  columnKeyMap,
  afastamentoDates,
  setAfastamentoDates,
  currentCompany,
  getSB,
  persistSettingIfAuthenticated,
  getVerifiedDate,
  addLog,
  broadcastNotifications,
  canEditEmployees = true,
}: UseBoardDnDParams) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [sourceContainerId, setSourceContainerId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!canEditEmployees) return;
      setActiveId(event.active.id as string);
      const container = findContainer(event.active.id as string, items);
      setSourceContainerId(container || null);
    },
    [items, canEditEmployees],
  );

  const handleDragOver = useCallback(
    (ev: DragOverEvent) => {
      if (!canEditEmployees) return;
      const { active, over } = ev;
      if (!over || active.id === over.id) return;
      const activeContainer = findContainer(active.id as string, items);
      const overContainer = findContainer(over.id as string, items);
      if (!activeContainer || !overContainer || activeContainer === overContainer) return;
      const translatedTop = active.rect.current.translated?.top;
      const overRect = over.rect;
      setItems((prev) => {
        const activeItems = prev[activeContainer];
        const overItems = prev[overContainer];
        const activeIndex = activeItems.findIndex((i) => i.id === active.id);
        const overIndex = overItems.findIndex((i) => i.id === over.id);
        const newIndex =
          over.id in prev
            ? overItems.length + 1
            : overIndex +
              (translatedTop != null && overRect && translatedTop > overRect.top + overRect.height
                ? 1
                : 0);
        return {
          ...prev,
          [activeContainer]: prev[activeContainer].filter((item) => item.id !== active.id),
          [overContainer]: [
            ...prev[overContainer].slice(0, newIndex),
            activeItems[activeIndex],
            ...prev[overContainer].slice(newIndex),
          ],
        };
      });
    },
    [items, setItems, canEditEmployees],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!canEditEmployees) {
        setActiveId(null);
        setSourceContainerId(null);
        return;
      }
      const { active, over } = event;
      const finalContainer = over ? findContainer(over.id as string, items) : null;

      if (sourceContainerId && finalContainer && sourceContainerId !== finalContainer) {
        const activeItem = (Object.values(items).flat() as Employee[]).find(
          (i) => i.id === active.id,
        );
        if (activeItem) {
          const fromCol =
            columns.find((c) => c.id === sourceContainerId)?.title || sourceContainerId;
          const toCol = columns.find((c) => c.id === finalContainer)?.title || finalContainer;
          addLog('Movimentação', `Moveu ${activeItem.name} de "${fromCol}" para "${toCol}"`);
          void broadcastNotifications([
            buildEmployeeMoveNotification(activeItem.name, fromCol, toCol),
          ]);

          const awayCols = ['afastados', 'afastados_adm'];
          const sb = getSB();

          if (awayCols.includes(finalContainer) && !awayCols.includes(sourceContainerId)) {
            const newDates = {
              ...afastamentoDates,
              [active.id as string]: getVerifiedDate().toISOString(),
            };
            setAfastamentoDates(newDates);
            if (sb && currentCompany) {
              void persistSettingIfAuthenticated('afastamento_dates', newDates, currentCompany);
            }
          } else if (
            !awayCols.includes(finalContainer) &&
            awayCols.includes(sourceContainerId)
          ) {
            const newDates = { ...afastamentoDates };
            delete newDates[active.id as string];
            setAfastamentoDates(newDates);
            if (sb && currentCompany) {
              void persistSettingIfAuthenticated('afastamento_dates', newDates, currentCompany);
            }
          }

          if (sb && columnKeyMap[finalContainer]) {
            dbMoveEmployee(sb, active.id as string, finalContainer, columnKeyMap).catch(
              console.warn,
            );
          }
        }
      }

      setItems((prev) => sortItemsByEmployeeName(prev));
      setActiveId(null);
      setSourceContainerId(null);
    },
    [
      items,
      sourceContainerId,
      columns,
      afastamentoDates,
      setAfastamentoDates,
      columnKeyMap,
      currentCompany,
      getSB,
      persistSettingIfAuthenticated,
      getVerifiedDate,
      addLog,
      broadcastNotifications,
      setItems,
      canEditEmployees,
    ],
  );

  const activeItem = activeId
    ? (Object.values(items).flat() as Employee[]).find((item) => item.id === activeId)
    : null;

  return {
    sensors,
    activeId,
    activeItem,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
